import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api, type Dashboard as DashboardData } from '../api';
import { Badge, Button, Card, ErrorMessage, Notice, PageHeader, Spinner, Stat, money, number, plural, when } from '../ui';

const COND = { N: 'New', U: 'Used' } as const;

/** A horizontal bar whose length is `value` against the largest in its group. */
function Bar({ value, max, tone = 'bg-sky-500' }: { value: number; max: number; tone?: string }) {
  return (
    <div className="h-2 w-full rounded bg-slate-100">
      <div className={`h-2 rounded ${tone}`} style={{ width: `${max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0}%` }} />
    </div>
  );
}

const monthLabel = (m: string) => new Date(`${m}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'short' });

function change(now: string, before: string) {
  const a = Number(now);
  const b = Number(before);
  if (b === 0) return a === 0 ? 'Same as the 30 days before' : 'Nothing in the 30 days before';
  const pct = Math.round(((a - b) / b) * 100);
  return `${pct >= 0 ? '+' : ''}${pct}% on the 30 days before`;
}

export function Dashboard() {
  // Always fresh: refetch on every visit and when you come back to the tab, and every minute while it stays open.
  const d = useQuery({ queryKey: ['dashboard'], queryFn: () => api<DashboardData>('/api/v1/dashboard'), staleTime: 0, refetchOnMount: 'always', refetchOnWindowFocus: true, refetchInterval: 60_000 });
  if (d.isPending) return <Spinner />;
  if (d.error || !d.data) return <ErrorMessage error={d.error} />;
  const x = d.data;
  const empty = x.lots === 0;
  const maxMonth = Math.max(...x.spend.byMonth.map((m) => Number(m.spend)), 0);
  const maxCat = Math.max(...x.topCategories.map((c) => Number(c.listValue) || Number(c.costValue)), 0);
  const attention = [
    { label: 'Priced below cost', n: x.pricing.losing, to: '/pricing?kind=loss', bad: true },
    { label: 'Thin margin (under 20%)', n: x.pricing.thin, to: '/pricing?kind=thin' },
    { label: 'Lots with no price', n: x.pricing.unpriced, to: '/pricing?kind=unpriced' },
    { label: 'Lots with stock that has no bin', n: x.lotsWithoutLocation, to: '/put-away' },
    { label: 'Part-outs with parts left to count', n: x.partOuts.open, to: '/reports/part-outs', note: x.partOuts.piecesLeft > 0 ? `${number(x.partOuts.piecesLeft)} pieces` : undefined },
    { label: 'Overdue inbound shipments', n: x.inboundShipments.overdue, to: '/reports/inbound-shipments?by=overdue', bad: true },
  ];

  return (
    <>
      <PageHeader title="Dashboard" actions={<div className="flex items-center gap-3"><span className="text-xs text-slate-500">Updated {new Date(d.dataUpdatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span><Button variant="secondary" disabled={d.isFetching} onClick={() => void d.refetch()}>{d.isFetching ? 'Refreshing…' : 'Refresh dashboard'}</Button><Link to="/reports" className="text-sm font-medium underline">All reports</Link></div>} />
      <div className="space-y-4">
        {x.catalogueItems === 0 && (
          <Notice tone="amber">
            The catalogue is empty, so there is nothing to search when adding stock.{' '}
            <Link to="/channels" className="font-medium underline">
              Load it from Channels
            </Link>
            .
          </Notice>
        )}
        {empty && x.catalogueItems > 0 && (
          <Notice>
            No stock yet. <Link to="/purchases" className="font-medium underline">Record a purchase</Link> and add stock to it, or bring your existing BrickLink store in from{' '}
            <Link to="/channels" className="font-medium underline">Channels</Link>.
          </Notice>
        )}

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-5">
          <Stat label="Stock at cost" value={money(x.stock.costValue)} note="What it cost you (stock added with no price counts as free)" />
          <Stat label="Stock at your prices" value={money(x.stock.listValue)} note={`${x.stock.pricedPct}% of pieces have a price`} />
          <Stat label="Potential profit" value={money(x.stock.potentialProfit)} note="If it all sold at your prices, before fees" />
          <Stat label="Pieces" value={number(x.pieces)} note={`${plural(x.lots, 'lot')} in stock`} />
          <Stat
            label="Sales, last 30 days"
            value={x.sales.everImported ? money(x.sales.revenue) : '—'}
            note={
              x.sales.everImported ? (
                <Link to="/sales" className="underline">
                  {plural(x.sales.orders, 'order')} · profit {money(x.sales.profit)}
                </Link>
              ) : (
                <Link to="/sales" className="underline">Bring in your BrickLink sales</Link>
              )
            }
          />
          <Stat label="Spent, last 30 days" value={money(x.spend.last30)} note={change(x.spend.last30, x.spend.previous30)} />
          <Stat label="Spent this year" value={money(x.spend.thisYear)} note={`${plural(x.purchases, 'purchase')} in all`} />
          <Stat
            label="On order"
            value={money(x.onOrder.value)}
            note={
              x.onOrder.orders > 0 ? (
                <Link to="/purchases?status=coming_in" className="underline">
                  {number(x.onOrder.pieces)} pieces on {plural(x.onOrder.orders, 'order')}
                </Link>
              ) : (
                'Nothing coming in'
              )
            }
          />
          {x.onOrder.awaitingCount.pieces > 0 && (
            <Stat
              label="Arrived, to be counted"
              value={number(x.onOrder.awaitingCount.pieces)}
              note={<Link to="/reports/inbound-shipments?by=arrived" className="underline">{plural(x.onOrder.awaitingCount.shipments, 'parcel')} here, not in stock yet</Link>}
            />
          )}
          <Stat label="Parts due this week" value={number(x.inboundShipments.piecesDueThisWeek)} note={<Link to="/reports/inbound-shipments?by=due" className="underline">{plural(x.inboundShipments.dueThisWeek, 'shipment')} due</Link>} />
          <Stat label="Tied up in old stock" value={money(x.stock.oldCost)} note={<Link to="/reports/age-buckets" className="underline">At cost, in stock over 6 months</Link>} />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card title="Needs attention">
            <ul className="space-y-2 text-sm">
              {attention.map((a) => (
                <li key={a.label} className="flex items-center justify-between gap-3">
                  <Link to={a.to} className="hover:underline">
                    {a.label}
                  </Link>
                  <span className="flex items-center gap-2 text-slate-600">
                    {a.note && a.n > 0 && <span className="text-xs">{a.note}</span>}
                    {a.n > 0 ? <Badge tone={a.bad ? 'red' : 'amber'}>{number(a.n)}</Badge> : <Badge tone="green">None</Badge>}
                  </span>
                </li>
              ))}
              <li className="flex items-center justify-between gap-3 border-t border-slate-100 pt-2">
                <span>BrickLink market figures</span>
                <span className="text-xs text-slate-600">
                  {number(x.market.withFigures)} of {number(x.market.lotsInStock)} lots
                  {x.market.waiting > 0 ? `, ${number(x.market.waiting)} waiting` : ''}
                  {x.market.stale > 0 ? `, ${number(x.market.stale)} stale` : ''}
                </span>
              </li>
            </ul>
          </Card>

          <Card title="Spending, last 12 months" actions={<Link to="/reports/purchases-by-month" className="text-sm underline">Report</Link>}>
            <div className="flex h-32 items-end gap-1.5" role="img" aria-label="Spending by month">
              {x.spend.byMonth.map((m) => (
                <div key={m.month} className="flex h-full flex-1 flex-col items-center justify-end gap-1" title={`${m.month}: ${money(m.spend)}`}>
                  <div className="w-full rounded-t bg-sky-500" style={{ height: `${maxMonth > 0 ? Math.max(Number(m.spend) > 0 ? 3 : 0, Math.round((Number(m.spend) / maxMonth) * 100)) : 0}%` }} />
                </div>
              ))}
            </div>
            <div className="mt-1 flex gap-1.5 text-center text-[10px] text-slate-500">
              {x.spend.byMonth.map((m) => (
                <div key={m.month} className="flex-1">
                  {monthLabel(m.month)}
                </div>
              ))}
            </div>
          </Card>

          <Card title="Where the value is" actions={<Link to="/reports/valuation" className="text-sm underline">Report</Link>}>
            <ul className="space-y-2 text-sm">
              {x.topCategories.length === 0 && <li className="text-slate-500">No stock yet.</li>}
              {x.topCategories.map((c) => (
                <li key={c.name}>
                  <div className="flex justify-between gap-3">
                    <span className="truncate">{c.name}</span>
                    <span className="tabular-nums text-slate-600">
                      {money(c.listValue)} <span className="text-xs">· {number(c.pieces)} pieces</span>
                    </span>
                  </div>
                  <Bar value={Number(c.listValue) || Number(c.costValue)} max={maxCat} />
                </li>
              ))}
            </ul>
          </Card>

          <Card title="Biggest lots, at your prices">
            <ul className="space-y-1.5 text-sm">
              {x.topLots.length === 0 && <li className="text-slate-500">No priced stock yet.</li>}
              {x.topLots.map((l) => (
                <li key={l.lotId} className="flex justify-between gap-3">
                  <Link to={`/lots/${l.lotId}`} className="min-w-0 truncate hover:underline">
                    {l.itemNo} · {l.color} · {COND[l.condition as 'N' | 'U']} <span className="text-slate-500">× {number(l.qty)}</span>
                  </Link>
                  <span className="tabular-nums">{money(l.listValue)}</span>
                </li>
              ))}
            </ul>
          </Card>

          <Card title="Who you buy from, last 12 months" actions={<Link to="/reports/purchases-by-supplier" className="text-sm underline">Report</Link>}>
            <ul className="space-y-1.5 text-sm">
              {x.topSuppliers.length === 0 && <li className="text-slate-500">No purchases in the last year.</li>}
              {x.topSuppliers.map((s) => (
                <li key={s.name} className="flex justify-between gap-3">
                  {s.id ? (
                    <Link to={`/suppliers/${s.id}`} className="truncate hover:underline">
                      {s.name}
                    </Link>
                  ) : (
                    <span>{s.name}</span>
                  )}
                  <span className="tabular-nums text-slate-600">
                    {money(s.spend)} <span className="text-xs">· {plural(s.orders, 'order')}</span>
                  </span>
                </li>
              ))}
            </ul>
          </Card>

          <Card title="Channels" actions={<Link to="/channels" className="text-sm underline">Manage</Link>}>
            <ul className="space-y-2 text-sm">
              {x.channels.length === 0 && <li className="text-slate-500">No channel connected yet.</li>}
              {x.channels.map((c) => (
                <li key={c.kind} className="flex items-center justify-between gap-3">
                  <span>{c.kind === 'bricklink' ? 'BrickLink' : c.kind === 'brickowl' ? 'Brick Owl' : c.kind}</span>
                  <span className="flex items-center gap-2 text-slate-600">
                    {c.mode === 'off' ? <Badge>Off</Badge> : c.mode === 'read_write' ? <Badge tone="amber">Read and write</Badge> : <Badge tone="blue">Read only</Badge>}
                    <span>{number(c.listings)} lots · last read {when(c.lastPullAt)}</span>
                    {c.unmatched > 0 && <Badge tone="amber">{number(c.unmatched)} not in BrickLoop</Badge>}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </>
  );
}
